import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '../api/client';
import type { LeadAttachment } from '../api/types';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { attachmentAccept, documentCategories, fmtBytes, maxAttachmentBytes, photoCategories, previewable } from './attachmentFormat';

// AttachmentsPanel: Fotos und Dateien zum Lead bzw. Termin (Dominik 14.10.2026: „Bilder und Dokumente müssen upload-fähig
// sein“) – am Handy direkt aus der Kamera oder als Datei, mehrere auf einmal; Fotos als Vorschau, Unterlagen als Liste.
// Einordnung und Titel lassen sich ändern, Ausblenden fragt nach.
export function AttachmentsPanel({ leadId, appointmentId }: { leadId: string; appointmentId?: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const key = projectKeys.leadAttachments(project.id, leadId);
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => papi.leadAttachments(leadId, signal) });
  const [category, setCategory] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState<string[]>([]);
  const editable = can('document.edit');

  const upload = async (files: FileList | null) => {
    const chosen = Array.from(files ?? []);
    if (chosen.length === 0) return;
    setFailed([]);
    setProgress({ done: 0, total: chosen.length });
    const errors: string[] = [];
    for (const [i, file] of chosen.entries()) {
      if (file.size > maxAttachmentBytes) {
        errors.push(`${file.name}: größer als 25 MB`);
      } else {
        try {
          await papi.uploadLeadAttachment(leadId, file, { name: file.name, category: category || undefined, appointment_id: appointmentId });
        } catch (e) {
          errors.push(`${file.name}: ${e instanceof ApiError ? e.message : 'Hochladen fehlgeschlagen'}`);
        }
      }
      setProgress({ done: i + 1, total: chosen.length });
    }
    setProgress(null);
    setFailed(errors);
    void queryClient.invalidateQueries({ queryKey: key });
  };

  const items = list.data?.items ?? [];
  const photos = items.filter((a) => a.kind === 'PHOTO');
  const docs = items.filter((a) => a.kind === 'DOCUMENT');
  return (
    <section className="attachments" aria-label="Fotos und Dateien">
      <div className="attachments-head">
        <h3>Fotos &amp; Dateien</h3>
        {editable && (
          <div className="attachments-actions">
            <label className="field attachments-category">
              <span>Einordnung</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">automatisch</option>
                <optgroup label="Fotos">
                  {photoCategories.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Unterlagen">
                  {documentCategories.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
            <label className={`btn btn-small btn-primary file-button${progress ? ' disabled' : ''}`}>
              Fotos hinzufügen
              <input
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                disabled={!!progress}
                onChange={(e) => {
                  void upload(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <label className={`btn btn-small file-button${progress ? ' disabled' : ''}`}>
              Datei hinzufügen
              <input
                type="file"
                accept={attachmentAccept}
                multiple
                disabled={!!progress}
                onChange={(e) => {
                  void upload(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        )}
      </div>
      {progress && (
        <p className="muted small" role="status">
          Wird hochgeladen … {progress.done} von {progress.total}
        </p>
      )}
      {failed.length > 0 && (
        <ul className="attachments-errors" role="alert">
          {failed.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {list.isError && <MutationError error={list.error} />}
      {list.isSuccess && items.length === 0 && (
        <p className="muted small">
          Noch keine Fotos oder Dateien.{editable && ' Am Handy öffnet „Fotos hinzufügen“ auch die Kamera.'}
        </p>
      )}
      {photos.length > 0 && (
        <ul className="attachment-photos">
          {photos.map((a) => (
            <li key={a.id}>
              <a href={papi.leadAttachmentUrl(a.id)} target="_blank" rel="noreferrer" className="attachment-thumb">
                {previewable(a.content_type) ? <img src={papi.leadAttachmentUrl(a.id)} alt={a.title || a.category_label} loading="lazy" /> : <span>{a.file_name}</span>}
              </a>
              <AttachmentMeta a={a} appointmentId={appointmentId} editable={editable} leadId={leadId} />
            </li>
          ))}
        </ul>
      )}
      {docs.length > 0 && (
        <ul className="attachment-docs">
          {docs.map((a) => (
            <li key={a.id}>
              <a href={papi.leadAttachmentUrl(a.id)} target="_blank" rel="noreferrer" className="attachment-doc-link">
                {a.title || a.file_name}
              </a>
              <AttachmentMeta a={a} appointmentId={appointmentId} editable={editable} leadId={leadId} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AttachmentMeta({ a, appointmentId, editable, leadId }: { a: LeadAttachment; appointmentId?: string; editable: boolean; leadId: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(a.title);
  const [category, setCategory] = useState(a.category);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: projectKeys.leadAttachments(project.id, leadId) });
  const save = useMutation({
    mutationFn: () => papi.updateLeadAttachment(a.id, { title, category }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: () => papi.deleteLeadAttachment(a.id), onSuccess: refresh });
  const options = a.kind === 'PHOTO' ? [...photoCategories, ...documentCategories] : [...documentCategories, ...photoCategories];
  return (
    <div className="attachment-meta">
      <span className="attachment-title">
        {a.kind === 'PHOTO' && <strong>{a.title || a.category_label}</strong>}
        {a.kind === 'DOCUMENT' && a.title && <span className="muted small">{a.file_name}</span>}
      </span>
      <span className="muted small">
        {[a.kind === 'DOCUMENT' || a.title ? a.category_label : '', a.kind === 'DOCUMENT' ? fmtBytes(a.size_bytes) : '', fmtDateTime(a.uploaded_at), a.uploaded_by_name]
          .filter(Boolean)
          .join(' · ')}
      </span>
      {appointmentId && a.appointment_id === appointmentId && <span className="badge badge-muted">dieser Termin</span>}
      {editable && !editing && (
        <span className="attachment-actions">
          <button type="button" className="link-button small" onClick={() => setEditing(true)}>
            Ändern
          </button>
          <button
            type="button"
            className="link-button small"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`„${a.title || a.file_name}“ ausblenden?`)) remove.mutate();
            }}
          >
            Ausblenden
          </button>
        </span>
      )}
      {editing && (
        <form
          className="attachment-edit"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <input type="text" value={title} maxLength={120} placeholder="Titel, z. B. Wohnzimmer" aria-label="Titel" onChange={(e) => setTitle(e.target.value)} />
          <select value={category} aria-label="Einordnung" onChange={(e) => setCategory(e.target.value as LeadAttachment['category'])}>
            {options.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-small btn-primary" disabled={save.isPending}>
            Speichern
          </button>
          <button type="button" className="btn btn-small" onClick={() => setEditing(false)}>
            Abbrechen
          </button>
        </form>
      )}
      {(save.error ?? remove.error) && <MutationError error={save.error ?? remove.error} />}
    </div>
  );
}
